import { createHttpClient } from '../../../api/http';
import { serviceUrls } from '../../../config/serviceUrls';

const client = createHttpClient(serviceUrls.gateway);

export type TutorSystemCapabilities = {
  live_ai_quota_pool_enabled: boolean;
  school_ai_allocations_enabled?: boolean;
  governance_mode?: 'flexible' | 'strict';
  quota_runtime_v2_enabled?: boolean;
};

export const TUTOR_SYSTEM_CAPABILITIES_TTL_MS = 5 * 60 * 1000;

type CacheEntry = { data: TutorSystemCapabilities; fetchedAt: number };

const cacheByTenant = new Map<string, CacheEntry>();
const inflightByTenant = new Map<string, Promise<TutorSystemCapabilities>>();

function capabilitiesTenantKey(): string {
  try {
    return (localStorage.getItem('X_TENANT_ID') || '').trim() || 'no-tenant';
  } catch {
    return 'no-tenant';
  }
}

function normalizeCapabilities(raw: TutorSystemCapabilities | undefined): TutorSystemCapabilities {
  return {
    live_ai_quota_pool_enabled: !!raw?.live_ai_quota_pool_enabled,
    school_ai_allocations_enabled: !!raw?.school_ai_allocations_enabled,
    governance_mode: raw?.governance_mode === 'strict' ? 'strict' : 'flexible',
    quota_runtime_v2_enabled: !!raw?.quota_runtime_v2_enabled,
  };
}

export async function fetchTutorSystemCapabilities(options?: {
  force?: boolean;
}): Promise<TutorSystemCapabilities> {
  const key = capabilitiesTenantKey();
  if (!options?.force) {
    const cached = cacheByTenant.get(key);
    if (cached && Date.now() - cached.fetchedAt < TUTOR_SYSTEM_CAPABILITIES_TTL_MS) {
      return cached.data;
    }
    const inflight = inflightByTenant.get(key);
    if (inflight) return inflight;
  }

  const run = (async () => {
    const res = await client.get<TutorSystemCapabilities>('/v1/tutor/system-capabilities');
    const data = normalizeCapabilities(res.data);
    cacheByTenant.set(key, { data, fetchedAt: Date.now() });
    return data;
  })();

  inflightByTenant.set(key, run);
  try {
    return await run;
  } catch (err) {
    cacheByTenant.delete(key);
    throw err;
  } finally {
    if (inflightByTenant.get(key) === run) {
      inflightByTenant.delete(key);
    }
  }
}

export function clearTutorSystemCapabilitiesCache(): void {
  cacheByTenant.clear();
  inflightByTenant.clear();
}
